import axios from "@/utils/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface GetSingRequestFilters {
  search?: string;
  page?: number;
  per_page?: number;
  order_by?: string;
  relationships?: string[];
}

export interface ISignDocument {
  id: string;
  sign_type: string;
  sign_method: string;
  status: string;
  node_execution_id: string;
  signers: OptionalSigners;
  requiredSigners: OptionalSigners;
  optionalSigners: OptionalSigners;
  signedBy: OptionalSigners;
  signedDocuments: OptionalSigners;
  latestSignedDocument?: LastedSignedDocument | null;
  nodeExecution: NodeExecution;
  first_signed_at: null;
  last_signed_at: null;
  created_at: Date;
  updated_at: Date;
  filename: string;
  signable?: Signable;
}

export interface LastedSignedDocument {
  id: string;
  status: string;
  signer_ip: string;
  version: number;
  generated_document_id: string;
  sign_request_id: string;
  signed_by: string;
  file: File;
  created_at: Date;
  updated_at: Date;
  signer: Datum;
}
export interface Signable {
  id: string;
  file: IFile;
}

export interface IFile {
  id: string;
  name: string;
  file_name: string;
  size: number;
  formatted_file_name?: string;
}

export interface File {
  id: string;
  name: string;
  url: string;
  file_name: string;
  formatted_file_name?: string;
  filename?: string;
  collection: string;
  size: number;
  model_id: string;
  model_type: string;
  created_by: null;
  updated_at: Date;
  created_at: Date;
}

export interface NodeExecution {
  id: string;
  node_id: string;
  flow_execution_id: string;
  started_by_type: string;
  started_by_id: string;
  started_at: Date;
  ended_at: null;
  status: string;
  context: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: null;
  resolved_at: null;
  resolved_by: null;
  called_by: string;
}

export interface OptionalSigners {
  data: Datum[];
}

export interface Datum {
  id: string;
  name: string;
  last_name: string;
  email: string;
  phone_number: string;
  dui: string;
  is_blocked: boolean;
  identification_type: null | string;
  keycloak_id: string;
  user_status: string;
  institution: Department;
  department: Department;
  position: Department;
  updated_at: Date;
  created_at: Date;
  deleted_at: Date | null;
  pivot: Pivot;
  signer_ip: string;
  sign_request_id: string;
  status: string;
  signed_by: string;
  file?: File;
  version?: number;
}

export interface Department {
  id: string;
  name: string;
}

export interface Pivot {
  sign_request_id: string;
  user_id: string;
  position: number;
  type: string;
  signed_at: Date | null;
  signer_ip: string | null;
  created_at: Date;
  updated_at: Date;
}

export const useGetSingRequest = (
  tramiteId: string,
  props: GetSingRequestFilters,
  refreshInterval = 0,
) => {
  return useQuery<{ data: ISignDocument[] }>({
    queryKey: ["sing-request", tramiteId, props],
    queryFn: async (): Promise<{ data: ISignDocument[] }> => {
      const response = await axios.get("/admin/v1/sign-requests", {
        params: {
          ...props,
          "filter[formality_request_id]": tramiteId,
          relationships: props.relationships
            ? props.relationships.join(",")
            : undefined,
        },
      });
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled: !!tramiteId,
  });
};

export const useGetSingRequestId = (
  singRequestId: string | undefined,
  refreshInterval = 0,
) => {
  return useQuery<unknown>({
    queryKey: ["sing-request-id", singRequestId],
    queryFn: async (): Promise<unknown> => {
      const response = await axios.get(
        `/admin/v1/sign-requests/${singRequestId}`,
      );
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled: !!singRequestId,
  });
};

export const useSignDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { sign_request_id: string }) => {
      const response = await axios.post("admin/v1/signed-documents", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sing-request"],
      });
    },
  });
};

export const useDownloadOriginalDocument = () => {
  return useMutation({
    mutationFn: async (data: { id: string }) => {
      const response = await axios.post("admin/v1/sign-requests/download", {
        sign_request_id: data.id,
      });
      return response.data;
    },
    onSuccess: (data) => {
      window.open(data.url || data, "_blank");
    },
  });
};

export const useDownloadSignedDocument = () => {
  return useMutation({
    mutationFn: async (data: { id: string }) => {
      const response = await axios.post("admin/v1/signed-documents/download", {
        signed_document_id: data.id,
      });
      return response.data;
    },
    onSuccess: (data) => {
      window.open(data.url || data, "_blank");
    },
  });
};
